import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { SITE_URL } from "@/lib/seo";
import { toolEditorial } from "@/lib/utility-tools/editorial";
import { ToolIsland } from "@/components/utility-tools/ToolIsland";
import { getTool, relatedTools, isToolIndexable, CLUSTER_LABEL, CLUSTER_EMOJI } from "@/lib/utility-tools/registry";
import { quickAnswers, buildScenarios, type QuickAnswer, type ToolScenario } from "@/lib/utility-tools/longtail";
import { buildCalculatorLd } from "@/lib/page-schemas";
import { CalculatorFaqAccordion, HowToUseCalculator, RelatedCalculators } from "@/components/calculator/CalculatorEducation";

export const Route = createFileRoute("/tools/$tool")({
  loader: ({ params }) => {
    const spec = getTool(params.tool);
    if (!spec) throw notFound();
    return {
      slug: spec.slug,
      answers: quickAnswers(spec),
      scenarios: buildScenarios(spec),
    };
  },
  head: ({ params }) => {
    const spec = getTool(params.tool);
    if (!spec) return leafHead({ path: `/tools/${params.tool}`, title: "Tool not found — DrHint", description: "This tool does not exist.", noindex: true });
    const url = `${SITE_URL}/tools/${spec.slug}`;
    return leafHead({
      path: `/tools/${spec.slug}`,
      title: spec.title,
      description: spec.description,
      // Off-topic clusters stay usable and internally linked, but are not
      // submitted for indexing — see INDEXABLE_CLUSTERS in the registry.
      noindex: !isToolIndexable(spec),
       scripts: buildCalculatorLd({
         url,
         name: spec.name,
         description: spec.description,
         applicationCategory: spec.cluster === "fitness" ? "HealthApplication" : spec.cluster === "finance" || spec.cluster === "business" ? "FinanceApplication" : "UtilitiesApplication",
         featureList: ["Instant calculation", "Shareable input link", "Printable report", "Step-by-step calculation logic"],
         howTo: {
           name: `How to use the ${spec.name}`,
           totalTime: "PT2M",
           steps: [
             { name: "Enter your details", text: `Complete the ${spec.inputs.length} labelled input fields using the units shown.` },
             { name: "Review the live calculation", text: "Read the result cards, comparison visual, and intermediate calculation steps." },
             { name: "Save or share", text: "Copy the results, print a PDF report, or create a link that preserves the active inputs." },
           ],
         },
         faqs: spec.faqs,
         trail: [
           { name: "Home", item: `${SITE_URL}/` },
           { name: "Tools", item: `${SITE_URL}/tools/all` },
           { name: spec.name, item: url },
         ],
       }),
    });
  },
  notFoundComponent: () => (
    <Layout>
      <section className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="text-2xl font-bold">Tool not found</h1>
        <Link to="/tools/all" className="mt-3 inline-block text-primary underline">
          Browse all 50 tools
        </Link>
      </section>
    </Layout>
  ),
  component: ToolPage,
});

function ToolPage() {
  const { slug, answers, scenarios } = Route.useLoaderData() as {
    slug: string;
    answers: QuickAnswer[];
    scenarios: ToolScenario[];
  };
  const spec = getTool(slug)!;
  const related = relatedTools(spec, 6);
  // Hand-written method / worked example / limitations, where one exists.
  const editorial = toolEditorial(slug);

  return (
    <Layout>
      <article className="mx-auto max-w-4xl px-4 py-6">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span className="mx-1">›</span>
          <Link to="/tools/all" className="hover:text-primary">Tools</Link>
          <span className="mx-1">›</span>
          <span className="text-foreground">{spec.name}</span>
        </nav>

        <h1 className="mt-3 text-3xl font-black leading-tight tracking-tight md:text-4xl">{spec.h1}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{spec.intro}</p>

        <div className="mt-5">
          <ToolIsland spec={spec} />
        </div>

         <HowToUseCalculator />

        {editorial && (
          <section className="mt-8 space-y-5">
            <div>
              <h2 className="text-lg font-bold">How this calculation works</h2>
              {editorial.how.map((para, i) => (
                <p key={i} className="mt-2 text-sm leading-relaxed text-muted-foreground">{para}</p>
              ))}
            </div>

            <div className="rounded-3xl border border-border bg-muted/30 p-5">
              <h3 className="text-base font-bold">Worked example — {editorial.example.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{editorial.example.body}</p>
            </div>

            <div>
              <h3 className="text-base font-bold">What this tool does not account for</h3>
              <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
                {editorial.limits.map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {spec.formula && (
          <section className="mt-8 rounded-3xl border border-border bg-muted/30 p-5">
            <h2 className="text-lg font-bold">Formula & step-by-step maths</h2>
            <div className="mt-3 space-y-2">
              {spec.formula.expressions.map((e, i) => (
                <div key={i} className="rounded-xl bg-background px-3 py-2 font-mono text-xs md:text-sm">
                  <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                  {e}
                </div>
              ))}
            </div>
            {spec.formula.variables && spec.formula.variables.length > 0 && (
              <dl className="mt-4 grid gap-2 sm:grid-cols-2">
                {spec.formula.variables.map((v) => (
                  <div key={v.name} className="text-xs">
                    <dt className="font-semibold">{v.name}</dt>
                    <dd className="text-muted-foreground">{v.meaning}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>
        )}

        <div className="prose prose-sm mt-8 max-w-none dark:prose-invert">
          {spec.sections.map((s) => (
            <section key={s.h}>
              <h2>{s.h}</h2>
              <p>{s.body}</p>
            </section>
          ))}
        </div>

        <section className="mt-8 overflow-x-auto">
          <h2 className="text-lg font-bold">{spec.staticTable.title}</h2>
          <table className="mt-3 w-full border-collapse text-sm">
            <thead>
              <tr>
                {spec.staticTable.head.map((h) => (
                  <th key={h} className="border-b border-border px-2 py-2 text-left text-xs uppercase text-muted-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {spec.staticTable.rows.map((r, i) => (
                <tr key={i}>
                  {r.map((cell, j) => (
                    <td key={j} className="border-b border-border/60 px-2 py-2">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {spec.staticTable.caption && (
            <p className="mt-2 text-xs text-muted-foreground">{spec.staticTable.caption}</p>
          )}
        </section>

        <CalculatorFaqAccordion faqs={spec.faqs} />

        {scenarios.length > 0 && (
          <section className="mt-10">
            <h2 className="text-lg font-bold">Three worked examples</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Same engine, three different starting points — useful if you want to see how sensitive the
              answer is before you type your own numbers in.
            </p>
            <div className="mt-4 space-y-4">
              {scenarios.map((sc, i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-4">
                  <h3 className="text-sm font-bold capitalize">
                    Example {i + 1}: {sc.label}
                  </h3>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    {sc.cards.map((c, j) => (
                      <div key={j} className="rounded-xl bg-muted/40 px-3 py-2">
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{c.label}</div>
                        <div className="text-sm font-bold">{c.value}</div>
                        {c.sub && <div className="text-[11px] text-muted-foreground">{c.sub}</div>}
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">{sc.note}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10">
          <h2 className="text-lg font-bold">Quick answers about the {spec.name}</h2>
          <div className="mt-3 space-y-5">
            {answers.map((a) => (
              <div key={a.q}>
                <h3 className="text-sm font-semibold">{a.q}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{a.a}</p>
              </div>
            ))}
          </div>
        </section>

        <RelatedCalculators items={related.map((tool) => ({ path: `/tools/${tool.slug}`, name: tool.name, description: tool.description, badge: CLUSTER_LABEL[tool.cluster], icon: CLUSTER_EMOJI[tool.cluster] }))} />
      </article>
    </Layout>
  );
}
